import { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  FaBullhorn,
  FaCheck,
  FaHeartbeat,
  FaHome,
  FaShoppingCart,
  FaTools,
  FaTooth,
} from "react-icons/fa";
import SectionEyebrow from "./SectionEyebrow";
import BrandButton from "./BrandButton";

const industries = [
  {
    icon: FaHeartbeat,
    title: "Healthcare",
    summary:
      "Clinics and care providers lose patients when calls go unanswered. We automate the front desk so every patient gets a quick, clear response.",
    challenges: [
      "Missed calls outside office hours",
      "No-shows from forgotten appointments",
      "Staff tied up with routine questions",
    ],
    builds: [
      "AI voice agent for booking and rescheduling",
      "Automated appointment reminders by SMS",
      "Twilio IVR to route calls to the right team",
      "Client portal to track calls and appointments",
      "Mobile app for patient bookings and updates",
    ],
    goal: "Fewer missed calls and fuller calendars.",
  },
  {
    icon: FaTooth,
    title: "Dental",
    summary:
      "A dental practice runs on its schedule. An AI receptionist keeps it full without adding to your front-desk workload.",
    challenges: [
      "Phones busy during procedures",
      "Slow confirmations and last-minute gaps",
      "Repeat questions about hours and services",
    ],
    builds: [
      "24/7 AI receptionist for bookings",
      "Confirmation and reminder messages",
      "Instant answers to common patient questions",
      "Warm transfer to staff when needed",
    ],
    goal: "A receptionist that never misses a call.",
  },
  {
    icon: FaHome,
    title: "Real Estate",
    summary:
      "Speed to lead decides who wins the listing. We help agents respond instantly and keep every prospect moving forward.",
    challenges: [
      "Leads going cold before the first reply",
      "Time lost scheduling viewings by hand",
      "Inconsistent follow-up after inquiries",
    ],
    builds: [
      "AI agent that qualifies new inquiries",
      "Viewing scheduling over call or SMS",
      "Automated follow-up sequences",
      "CRM workflows in GoHighLevel",
      "Custom web app for listings and lead dashboards",
    ],
    goal: "Faster replies and consistent follow-up.",
  },
  {
    icon: FaShoppingCart,
    title: "E-commerce & Retail",
    summary:
      "SMS reaches customers fast, but only if it is registered and compliant. We set up messaging that gets delivered.",
    challenges: [
      "Promotional texts blocked or filtered",
      "Unregistered numbers and carrier errors",
      "Manual order and delivery updates",
    ],
    builds: [
      "A2P 10DLC brand and campaign registration",
      "Compliant promotional SMS campaigns",
      "Order and delivery update messages",
      "Opt-in and STOP handling",
      "Custom web store features and a mobile shopping app",
    ],
    goal: "Messages that reach customers, not spam filters.",
  },
  {
    icon: FaTools,
    title: "Home Services",
    summary:
      "For plumbers, electricians and cleaners, a missed call is a lost job. We make sure every caller gets a response.",
    challenges: [
      "Calls missed while on the job",
      "Leads choosing the first business to reply",
      "Booking and quoting done after hours",
    ],
    builds: [
      "AI call answering and job booking",
      "Missed-call text-back automation",
      "Call routing with Twilio IVR",
      "Simple dashboards for calls and SMS",
      "Mobile app for job scheduling and field teams",
    ],
    goal: "Every missed call turned into a conversation.",
  },
  {
    icon: FaBullhorn,
    title: "Marketing Agencies",
    summary:
      "Agencies need a clean GoHighLevel setup they can resell and scale. We build the foundation and handle the compliance.",
    challenges: [
      "Messy or unstructured agency accounts",
      "A2P rejections that delay client launches",
      "Phone and Twilio configuration errors",
    ],
    builds: [
      "Branded agency workspace and sub-accounts",
      "Sales funnels and landing pages",
      "Twilio and phone system setup",
      "A2P brand and campaign registration",
      "Custom client dashboards and reporting web apps",
    ],
    goal: "Faster client onboarding, fewer compliance delays.",
  },
];

const reveal = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
};

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08 } },
};

const Industries = () => {
  const [active, setActive] = useState(0);
  const [fade, setFade] = useState({ left: false, right: false });
  const tabRefs = useRef([]);
  const tabListRef = useRef(null);
  const reduceMotion = useReducedMotion();
  const current = industries[active];
  const CurrentIcon = current.icon;

  const updateFade = useCallback(() => {
    const list = tabListRef.current;
    if (!list) return;
    const max = list.scrollWidth - list.clientWidth;
    const left = list.scrollLeft > 4;
    const right = list.scrollLeft < max - 4;
    setFade((prev) => (prev.left === left && prev.right === right ? prev : { left, right }));
  }, []);


  useEffect(() => {
    const list = tabListRef.current;
    if (!list) return undefined;

    const centerActive = (behavior) => {
      const tab = tabRefs.current[active];
      if (!tab || list.scrollWidth <= list.clientWidth) return;
      const left = tab.offsetLeft - (list.clientWidth - tab.offsetWidth) / 2;
      list.scrollTo({ left: Math.max(0, left), behavior });
    };

    centerActive(reduceMotion ? "auto" : "smooth");
    updateFade();

    const handleResize = () => {
      centerActive("auto");
      updateFade();
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [active, reduceMotion, updateFade]);

  const handleKeyDown = (event) => {
    const keys = ["ArrowDown", "ArrowRight", "ArrowUp", "ArrowLeft"];
    if (!keys.includes(event.key)) return;
    event.preventDefault();
    const forward = event.key === "ArrowDown" || event.key === "ArrowRight";
    const next = (active + (forward ? 1 : -1) + industries.length) % industries.length;
    setActive(next);
    tabRefs.current[next]?.focus();
  };

  const handleSelect = (index) => {
    setActive(index);
  };

  return (
    <section id="industries" className="overflow-x-hidden bg-white py-16 sm:py-20 lg:py-28">
      <div className="mx-auto max-w-[84rem] px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, amount: 0.2 }}
          variants={stagger}
          className="mx-auto mb-10 max-w-5xl text-center sm:mb-12"
        >
          <SectionEyebrow variants={reveal}>Industries We Support</SectionEyebrow>
          <motion.h2
            variants={reveal}
            className="mt-4 font-display text-3xl font-semibold leading-[1.15] text-slate-900 [text-wrap:balance] sm:text-4xl lg:text-[2.6rem] xl:text-5xl"
          >
            Solutions shaped around your industry
          </motion.h2>
          <motion.p variants={reveal} className="mx-auto mt-4 max-w-xl text-base leading-7 text-slate-600">
            Every business talks to customers differently. Pick your industry to see the problems we solve and what we build.
          </motion.p>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
         viewport={{ once: false, amount: 0.1 }}
          variants={reveal}
          className="relative w-full min-w-0 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_20px_48px_-32px_rgba(76,29,149,0.28)]"
        >
          <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 z-10 h-1 bg-[#8B4AFA]" />

          <div className="grid grid-cols-1 lg:grid-cols-[19rem_minmax(0,1fr)]">
            {/* Left: industry tabs */}
            <div className="relative min-w-0 border-b border-slate-200 bg-slate-50/70 pt-5 lg:border-b-0 lg:border-r lg:p-5 lg:pt-7">
              <div
                ref={tabListRef}
                role="tablist"
                aria-label="Industries"
                aria-orientation="vertical"
                onKeyDown={handleKeyDown}
                onScroll={updateFade}
                className="relative flex gap-2 overflow-x-auto px-3 pb-3 [-ms-overflow-style:none] [scrollbar-width:none] sm:px-4 lg:flex-col lg:overflow-visible lg:px-0 lg:pb-0 [&::-webkit-scrollbar]:hidden"
              >
                {industries.map(({ icon: Icon, title }, index) => {
                  const isActive = index === active;
                  return (
                    <button
                      key={title}
                      ref={(el) => (tabRefs.current[index] = el)}
                      type="button"
                      role="tab"
                      id={`industry-tab-${index}`}
                      aria-selected={isActive}
                      aria-controls="industry-panel"
                      tabIndex={isActive ? 0 : -1}
                      onClick={() => handleSelect(index)}
                      className={`group relative flex shrink-0 items-center gap-2.5 whitespace-nowrap rounded-xl px-3 py-2.5 text-left text-sm font-semibold transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#8B4AFA] focus-visible:ring-offset-2 sm:gap-3 sm:px-4 sm:py-3 ${
                        isActive ? "text-[#6e3fc7]" : "text-slate-600 hover:bg-white/80 hover:text-slate-900"
                      }`}
                    >
                      {isActive && (
                        <motion.span
                          layoutId="industry-active"
                          transition={reduceMotion ? { duration: 0 } : { type: "spring", stiffness: 380, damping: 32 }}
                          className="absolute inset-0 rounded-xl border border-[#8B4AFA]/30 bg-white shadow-[0_6px_16px_-10px_rgba(139,74,250,0.5)]"
                        />
                      )}
                      <span
                        className={`relative flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-sm transition-colors duration-200 ${
                         isActive ? "bg-[#8B4AFA] text-white" : "bg-[#8B4AFA]/10 text-[#8B4AFA] group-hover:bg-[#8B4AFA]/20"
                        }`}
                      >
                        <Icon aria-hidden="true" />
                      </span>
                      <span className="relative transition-transform duration-200 group-hover:translate-x-0.5 motion-reduce:transform-none">
                              {title}
                            </span>
                    </button>
                  );
                })}
              </div>

              {/* Edge fades: scroll hint for the tab strip (mobile/tablet only) */}
              <div
                aria-hidden="true"
                className={`pointer-events-none absolute inset-y-0 left-0 w-12 bg-gradient-to-r from-slate-50 to-transparent transition-opacity duration-200 lg:hidden ${
                  fade.left ? "opacity-100" : "opacity-0"
                }`}
              />
              <div
                aria-hidden="true"
                className={`pointer-events-none absolute inset-y-0 right-0 w-12 bg-gradient-to-l from-slate-50 to-transparent transition-opacity duration-200 lg:hidden ${
                  fade.right ? "opacity-100" : "opacity-0"
                }`}
              />
            </div>

            {/* Right: detail panel */}
            <div className="min-w-0 p-5 pt-7 sm:p-8 sm:pt-9 lg:p-10">
              <AnimatePresence mode="wait">
                <motion.div
                  key={active}
                  role="tabpanel"
                  id="industry-panel"
                  aria-labelledby={`industry-tab-${active}`}
                  initial={reduceMotion ? false : { opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={reduceMotion ? { opacity: 0 } : { opacity: 0, y: -8 }}
                  transition={{ duration: 0.25, ease: "easeOut" }}
                  className="min-w-0"
                >
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
                   <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-[#8B4AFA]/25 bg-[#8B4AFA]/10 text-lg text-[#8B4AFA] transition-colors duration-200 hover:bg-[#8B4AFA] hover:text-white">
                      <CurrentIcon aria-hidden="true" />
                    </span>
                    <div className="min-w-0">
                      <h3 className="font-display text-xl font-semibold text-slate-900 sm:text-2xl">
                        {current.title}
                      </h3>
                      <p className="mt-2 max-w-2xl text-sm leading-7 text-slate-600 sm:text-base">
                        {current.summary}
                      </p>
                    </div>
                  </div>

                  <div className="mt-7 grid grid-cols-1 gap-4 sm:mt-8 sm:gap-5 md:grid-cols-2">
                    <div className="min-w-0 rounded-xl border border-slate-200 bg-slate-50/60 p-4 transition-[border-color,background-color] duration-200 hover:border-slate-300 hover:bg-slate-50 sm:p-5">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                        Common challenges
                      </p>
                      <ul className="mt-4 space-y-3">
                        {current.challenges.map((item) => (
                         <li key={item} className="group flex items-start gap-3 text-sm leading-6 text-slate-600 transition-colors duration-200 hover:text-slate-900">
  <span aria-hidden="true" className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-slate-400 transition-colors duration-200 group-hover:bg-[#8B4AFA]" />
                            <span className="min-w-0">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                  <div className="min-w-0 rounded-xl border border-[#8B4AFA]/20 bg-[#8B4AFA]/[0.04] p-4 transition-[border-color,box-shadow] duration-200 hover:border-[#8B4AFA]/40 hover:shadow-[0_14px_30px_-22px_rgba(139,74,250,0.5)] sm:p-5">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#6e3fc7]">
                        What we build
                      </p>
                      <ul className="mt-4 space-y-3">
                        {current.builds.map((item) => (
                          <li key={item} className="group flex items-start gap-3 text-sm leading-6 text-slate-700 transition-colors duration-200 hover:text-slate-900">
  <span className="mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#8B4AFA]/15 text-[#8B4AFA] transition-colors duration-200 group-hover:bg-[#8B4AFA] group-hover:text-white">
                              <FaCheck aria-hidden="true" className="h-2.5 w-2.5" />
                            </span>
                            <span className="min-w-0">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <div className="mt-7 flex flex-col gap-4 border-t border-slate-200 pt-6 sm:mt-8 sm:flex-row sm:items-center sm:justify-between">
                    <p className="text-sm leading-6 text-slate-600">
                      <span className="font-semibold text-slate-900">Goal: </span>
                      {current.goal}
                    </p>
                    <BrandButton as={Link} to="/contact" className="self-start sm:self-auto">
                      Discuss Your Project
                    </BrandButton>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Industries;